import React, { useState } from 'react';

/**
 * =====================
 * MOCK DATA SECTION
 * =====================
 */

const TRIGGERS = [
    { value: 'health', label: 'Health Factor' },
    { value: 'price', label: 'Price' },
    { value: 'apy', label: 'APY' },
    { value: 'time', label: 'Time-based' },
    { value: 'DCA', label: 'DCA' },
];

const PROTOCOLS = [
    { value: 'ekubo', label: 'Ekubo' },
    { value: 'vesu', label: 'Vesu' },
    { value: 'extended', label: 'Extended' },
    { value: 'edure', label: 'Edure' },
];

const ACTION_TYPES = [
    { value: 'borrow', label: 'Borrow' },
    { value: 'lend', label: 'Lend' },
    { value: 'swap', label: 'Swap' },
    { value: 'stake', label: 'Stake' },
    { value: 'lp', label: 'Provide Liquidity' },
    { value: 'perps - long', label: 'Perps Long' },
    { value: 'perps - short', label: 'Perps Short' },
];

const TOKENS = [
    { value: 'ETH', label: 'ETH' },
    { value: 'USDC', label: 'USDC' },
    { value: 'STRK', label: 'STRK' },
    { value: 'xSRK', label: 'xSRK' },
];

/**
 * Protocol to allowed action types mapping
 */
const PROTOCOL_ACTIONS: Record<string, string[]> = {
    ekubo: ['swap', 'lp'],
    vesu: ['borrow', 'lend'],
    edure: ['stake'],
    extended: ['perps - long', 'perps - short'],
};

// Mock field configs for protocol/action
const ACTION_FIELDS: Record<string, Record<string, { label: string; type: string; options?: any[] }[]>> = {
    ekubo: {
        swap: [
            { label: 'Token', type: 'select', options: TOKENS },
            { label: 'Amount', type: 'number' },
            { label: 'Slippage (%)', type: 'number' },
        ],
        lp: [
            { label: 'Token', type: 'select', options: TOKENS },
            { label: 'Amount', type: 'number' },
        ],
    },
    vesu: {
        borrow: [
            { label: 'Token', type: 'select', options: TOKENS },
            { label: 'Collateral Fraction (%)', type: 'number' },
        ],
        lend: [
            { label: 'Token', type: 'select', options: TOKENS },
            { label: 'Amount', type: 'number' },
        ],
    },
    edure: {
        stake: [
            { label: 'Token', type: 'select', options: TOKENS },
            { label: 'Amount', type: 'number' },
        ],
    },
    extended: {
        'perps - long': [
            { label: 'Token', type: 'select', options: TOKENS },
            { label: 'Amount', type: 'number' },
            { label: 'Leverage', type: 'number' },
        ],
        'perps - short': [
            { label: 'Token', type: 'select', options: TOKENS },
            { label: 'Amount', type: 'number' },
            { label: 'Leverage', type: 'number' },
        ],
    },
};

// Example strategies
export const EXAMPLE_STRATEGIES = [
    {
        name: 'Leveraged STRK Staking',
        on: false,
        usedCount: 42,
        apy: 18.2,
        risk: 'High',
        strategy: {
            trigger: { type: 'DCA', value: '10' },
            actions: [
                {
                    protocol: 'edure',
                    type: 'stake',
                    amountMode: 'amount',
                    token: 'STRK',
                    amount: '1000',
                },
                {
                    type: 'repeat',
                    condition: { type: 'ltv', value: '2' },
                    actions: [
                        {
                            protocol: 'vesu',
                            type: 'lend',
                            amountMode: 'percent',
                            token: 'xSRK',
                            amount: '100',
                        },
                        {
                            protocol: 'vesu',
                            type: 'borrow',
                            token: 'STRK',
                            'loan to value (ltv%)': '70',
                            'collateral fraction (%)': '70',
                        },
                        {
                            protocol: 'edure',
                            type: 'stake',
                            amountMode: 'percent',
                            token: 'STRK',
                            amount: '100',
                        },
                    ],
                },
            ],
        },
    },
    {
        name: 'ETH/USDC LP DCA',
        on: false,
        usedCount: 31,
        apy: 7.5,
        risk: 'Medium',
        strategy: {
            trigger: { type: 'DCA', value: '7' },
            actions: [
                {
                    protocol: 'ekubo',
                    type: 'swap',
                    token: 'USDC',
                    amount: '500',
                    'slippage (%)': '0.5',
                },
                {
                    protocol: 'ekubo',
                    type: 'lp',
                    token: 'ETH',
                    amount: '0.2',
                },
            ],
        },
    },
    {
        name: 'Perps Yield Farming',
        on: true,
        usedCount: 19,
        apy: 24.1,
        risk: 'Very High',
        strategy: {
            trigger: { type: 'apy', value: '15' },
            actions: [
                {
                    protocol: 'extended',
                    type: 'perps - long',
                    token: 'ETH',
                    amount: '1',
                    leverage: '3',
                },
                {
                    protocol: 'extended',
                    type: 'perps - short',
                    token: 'USDC',
                    amount: '1000',
                    leverage: '2',
                },
            ],
        },
    },
];

/**
 * =====================
 * END MOCK DATA SECTION
 * =====================
 */

function ActionForm({ action, onChange, onRemove, selectable, selected, onSelect }: any) {
    // Filter action types based on protocol
    let allowedActions = ACTION_TYPES;
    if (action.protocol && PROTOCOL_ACTIONS[action.protocol]) {
        allowedActions = ACTION_TYPES.filter(a => PROTOCOL_ACTIONS[action.protocol].includes(a.value));
    }

    // If the current action type is not allowed for the selected protocol, reset it
    React.useEffect(() => {
        if (
            action.protocol &&
            action.type &&
            !PROTOCOL_ACTIONS[action.protocol]?.includes(action.type)
        ) {
            onChange({ ...action, type: '' });
        }
        // eslint-disable-next-line
    }, [action.protocol]);

    // Dynamic fields based on protocol/action
    let fields: any[] = [];
    if (action.protocol && action.type && ACTION_FIELDS[action.protocol]?.[action.type]) {
        fields = ACTION_FIELDS[action.protocol][action.type];
    } else {
        // fallback to generic fields
        fields = [
            { label: 'Token', type: 'select', options: TOKENS },
            { label: 'Amount', type: 'number' },
            { label: 'Slippage (%)', type: 'number' },
        ];
    }

    // Helper: is this field the 'Amount' for lend/stake?
    const isPctAmount = (field: any) => {
        return (
            (action.type === 'lend' || action.type === 'stake') && field.label === 'Amount'
        );
    };

    // Default to 'amountMode' = 'amount' if not set
    if ((action.type === 'lend' || action.type === 'stake') && !action.amountMode) {
        action.amountMode = 'amount';
    }

    return (
        <div className={`bg-gray-800 rounded-lg p-4 mb-4 flex flex-col gap-3 relative ${selectable ? 'pl-8' : ''}`}>
            {selectable && (
                <input
                    type="checkbox"
                    className="absolute left-2 top-5 scale-125 accent-cyan-500"
                    checked={selected}
                    onChange={onSelect}
                />
            )}
            <div className="flex gap-4">
                <div className="flex-1">
                    <label className="block text-sm text-gray-400 mb-1">Protocol</label>
                    <select
                        className="w-full bg-gray-900 text-white rounded p-2"
                        value={action.protocol}
                        onChange={e => onChange({ ...action, protocol: e.target.value })}
                    >
                        <option value="">Select</option>
                        {PROTOCOLS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                </div>
                <div className="flex-1">
                    <label className="block text-sm text-gray-400 mb-1">Action</label>
                    <select
                        className="w-full bg-gray-900 text-white rounded p-2"
                        value={action.type}
                        onChange={e => onChange({ ...action, type: e.target.value })}
                    >
                        <option value="">Select</option>
                        {allowedActions.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                    </select>
                </div>
                <button className="ml-2 text-red-400 hover:text-red-600" onClick={onRemove} title="Remove action">✕</button>
            </div>
            <div className="flex gap-4 flex-wrap">
                {fields.map((field, idx) => {
                    if (isPctAmount(field)) {
                        return (
                            <div className="flex-1 min-w-[120px]" key={idx}>
                                <label className="block text-sm text-gray-400 mb-1">{field.label}</label>
                                <div className="flex gap-2 items-center">
                                    <input
                                        type="number"
                                        className="w-full bg-gray-900 text-white rounded p-2"
                                        value={action.amount || ''}
                                        onChange={e => onChange({ ...action, amount: e.target.value })}
                                        min={action.amountMode === 'percent' ? '0' : '0'}
                                        max={action.amountMode === 'percent' ? '100' : undefined}
                                        placeholder={action.amountMode === 'percent' ? '% of balance' : 'Amount'}
                                    />
                                    <button
                                        type="button"
                                        className={`px-2 py-1 rounded text-xs font-semibold border ${action.amountMode === 'percent' ? 'bg-cyan-700 text-white' : 'bg-gray-700 text-gray-200'}`}
                                        onClick={() => onChange({ ...action, amountMode: action.amountMode === 'percent' ? 'amount' : 'percent', amount: '' })}
                                    >
                                        {action.amountMode === 'percent' ? '%' : 'Amt'}
                                    </button>
                                    {action.amountMode === 'percent' && <span className="ml-1 text-gray-400">%</span>}
                                </div>
                            </div>
                        );
                    }
                    // Default field rendering
                    return (
                        <div className="flex-1 min-w-[120px]" key={idx}>
                            <label className="block text-sm text-gray-400 mb-1">{field.label}</label>
                            {field.type === 'select' ? (
                                <select
                                    className="w-full bg-gray-900 text-white rounded p-2"
                                    value={action[field.label.toLowerCase()] || ''}
                                    onChange={e => onChange({ ...action, [field.label.toLowerCase()]: e.target.value })}
                                >
                                    <option value="">Select</option>
                                    {field.options.map((opt: any) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            ) : (
                                <input
                                    type={field.type}
                                    className="w-full bg-gray-900 text-white rounded p-2"
                                    value={action[field.label.toLowerCase()] || ''}
                                    onChange={e => onChange({ ...action, [field.label.toLowerCase()]: e.target.value })}
                                    min={field.type === 'number' ? '0' : undefined}
                                />
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function RepeatConditionForm({ condition, onChange }: any) {
    // Simple: allow user to pick type and value
    const CONDITION_TYPES = [
        { value: 'ltv', label: 'LTV Ratio' },
        { value: 'health', label: 'Health Factor' },
        { value: 'count', label: 'Number of Iterations' },
    ];
    return (
        <div className="flex gap-2 items-center mb-2">
            <label className="text-sm text-gray-400">Repeat until</label>
            <select
                className="bg-gray-900 text-white rounded p-2"
                value={condition.type}
                onChange={e => onChange({ ...condition, type: e.target.value })}
            >
                <option value="">Select</option>
                {CONDITION_TYPES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
            <input
                className="bg-gray-900 text-white rounded p-2 w-32"
                type="number"
                value={condition.value || ''}
                onChange={e => onChange({ ...condition, value: e.target.value })}
                placeholder="Value"
            />
        </div>
    );
}

function StrategyDiagram({ trigger, actions }: any) {
    // Simple vertical block diagram
    function renderActions(actions: any[]) {
        return actions.map((action, idx) => {
            if (action.type === 'repeat') {
                return (
                    <React.Fragment key={idx}>
                        <div className="bg-cyan-950 text-cyan-200 px-6 py-3 rounded-lg shadow flex flex-col items-center min-w-[180px] border-2 border-cyan-400">
                            <div className="font-semibold mb-1">Repeat Block</div>
                            <div className="text-xs text-cyan-300 mb-2">Until {action.condition?.type}: <span className="text-cyan-100">{action.condition?.value}</span></div>
                            <div className="flex flex-col gap-2 w-full">
                                {renderActions(action.actions)}
                            </div>
                        </div>
                        {idx < actions.length - 1 && <div className="w-1 h-6 bg-cyan-400 rounded-full" />}
                    </React.Fragment>
                );
            }
            // Normal action
            return (
                <React.Fragment key={idx}>
                    <div className="bg-gray-800 text-white px-6 py-3 rounded-lg shadow flex flex-col items-center min-w-[180px]">
                        <div className="font-semibold mb-1">{action.protocol ? PROTOCOLS.find(p => p.value === action.protocol)?.label : 'Protocol'} - {action.type || 'Action'}</div>
                        <div className="text-xs text-gray-300">
                            {Object.entries(action).filter(([k]) => !['protocol', 'type'].includes(k)).map(([k, v]) => (
                                v ? <div key={k}>{k}: <span className="text-cyan-300">{v}</span></div> : null
                            ))}
                        </div>
                    </div>
                    {idx < actions.length - 1 && <div className="w-1 h-6 bg-cyan-400 rounded-full" />}
                </React.Fragment>
            );
        });
    }
    return (
        <div className="p-6">
            <h2 className="text-xl font-bold mb-4 text-cyan-300">Strategy Visualization</h2>
            <div className="flex flex-col items-center gap-4">
                {/* Trigger block */}
                <div className="bg-cyan-900 text-white px-6 py-3 rounded-lg shadow font-semibold">
                    Trigger: {trigger.type} {trigger.value && (<span className="text-cyan-200">{trigger.value}</span>)}
                </div>
                {/* Arrow */}
                {actions.length > 0 && <div className="w-1 h-6 bg-cyan-400 rounded-full" />}
                {/* Action blocks (with repeat support) */}
                {renderActions(actions)}
            </div>
        </div>
    );
}

function RepeatBlockForm({ block, onChange, onRemove, onActionChange, onActionRemove }: any) {
    // block: { type: 'repeat', condition, actions }
    return (
        <div className="border-2 border-cyan-400 rounded-lg p-4 mb-4 bg-cyan-950">
            <div className="flex items-center justify-between mb-2">
                <div className="font-semibold text-cyan-200">Repeat Block</div>
                <button className="text-red-400 hover:text-red-600 text-sm" onClick={onRemove}>Remove</button>
            </div>
            <RepeatConditionForm
                condition={block.condition}
                onChange={cond => onChange({ ...block, condition: cond })}
            />
            <div className="mt-2">
                {block.actions.map((action: any, idx: number) => (
                    <ActionForm
                        key={idx}
                        action={action}
                        onChange={a => onActionChange(idx, a)}
                        onRemove={() => onActionRemove(idx)}
                        selectable={false}
                    />
                ))}
            </div>
        </div>
    );
}

const StrategyBuilder: React.FC = () => {
    const [mode, setMode] = useState<'builder' | 'json'>('builder');
    const [trigger, setTrigger] = useState('health');
    const [triggerValue, setTriggerValue] = useState('');
    const [actions, setActions] = useState<any[]>([]);
    const [json, setJson] = useState(JSON.stringify(EXAMPLE_STRATEGIES[0], null, 2));
    const [jsonError, setJsonError] = useState<string | null>(null);
    const [selectedActions, setSelectedActions] = useState<number[]>([]);
    const [showRepeatModal, setShowRepeatModal] = useState(false);
    const [repeatCondition, setRepeatCondition] = useState<any>({ type: '', value: '' });
    const [strategyName, setStrategyName] = useState('');
    const [savedStrategies, setSavedStrategies] = useState<any[]>(EXAMPLE_STRATEGIES);

    // Sync builder state to JSON
    React.useEffect(() => {
        if (mode === 'builder') {
            setJson(JSON.stringify({ trigger: { type: trigger, value: triggerValue }, actions }, null, 2));
        }
        // eslint-disable-next-line
    }, [trigger, triggerValue, actions, mode]);

    // Sync JSON to builder state
    React.useEffect(() => {
        if (mode === 'json') {
            try {
                const parsed = JSON.parse(json);
                setJsonError(null);
                if (parsed.trigger) {
                    setTrigger(parsed.trigger.type || 'health');
                    setTriggerValue(parsed.trigger.value || '');
                }
                if (Array.isArray(parsed.actions)) {
                    setActions(parsed.actions);
                }
            } catch (e: any) {
                setJsonError(e.message);
            }
        }
        // eslint-disable-next-line
    }, [json, mode]);

    const handleActionChange = (idx: number, newAction: any) => {
        setActions(actions => actions.map((a, i) => (i === idx ? newAction : a)));
    };
    const handleAddAction = () => {
        setActions(actions => [
            ...actions,
            { protocol: '', type: '' },
        ]);
    };
    const handleRemoveAction = (idx: number) => {
        setActions(actions => actions.filter((_, i) => i !== idx));
        setSelectedActions(selectedActions => selectedActions.filter(i => i !== idx));
    };

    // Repeat logic
    const handleSelectAction = (idx: number) => {
        setSelectedActions(selected =>
            selected.includes(idx)
                ? selected.filter(i => i !== idx)
                : [...selected, idx]
        );
    };
    const handleRepeatSelected = () => {
        setShowRepeatModal(true);
    };
    const handleApplyRepeat = () => {
        // Wrap selected actions in a repeat block
        const newActions: any[] = [];
        let repeatBlock: any = { type: 'repeat', condition: repeatCondition, actions: [] };
        actions.forEach((a, i) => {
            if (selectedActions.includes(i)) {
                repeatBlock.actions.push(a);
            } else {
                if (repeatBlock.actions.length > 0) {
                    newActions.push(repeatBlock);
                    repeatBlock = { type: 'repeat', condition: repeatCondition, actions: [] };
                }
                newActions.push(a);
            }
        });
        if (repeatBlock.actions.length > 0) {
            newActions.push(repeatBlock);
        }
        setActions(newActions);
        setSelectedActions([]);
        setShowRepeatModal(false);
        setRepeatCondition({ type: '', value: '' });
    };

    // Save current strategy
    const handleSave = () => {
        if (!strategyName.trim()) return;
        const newStrategy = {
            name: strategyName,
            on: false,
            strategy: {
                trigger: { type: trigger, value: triggerValue },
                actions,
            },
        };
        setSavedStrategies(strats => {
            // Overwrite if name exists
            const idx = strats.findIndex(s => s.name === strategyName);
            if (idx !== -1) {
                const updated = [...strats];
                updated[idx] = newStrategy;
                return updated;
            }
            return [...strats, newStrategy];
        });
    };

    // Load a saved strategy
    const handleLoad = (s: any) => {
        setStrategyName(s.name);
        setTrigger(s.strategy.trigger.type);
        setTriggerValue(s.strategy.trigger.value);
        setActions(s.strategy.actions);
        setMode('builder');
    };

    // Toggle on/off
    const handleToggle = (idx: number) => {
        setSavedStrategies(strats => strats.map((s, i) => i === idx ? { ...s, on: !s.on } : s));
    };

    // Helper to render actions and repeat blocks in the builder
    function renderBuilderActions(actions: any[], parentIdx: number[] = []) {
        return actions.map((action, idx) => {
            const key = [...parentIdx, idx].join('-');
            if (action.type === 'repeat') {
                // Render repeat block
                return (
                    <RepeatBlockForm
                        key={key}
                        block={action}
                        onChange={newBlock => {
                            setActions(actions => updateNested(actions, [...parentIdx, idx], newBlock));
                        }}
                        onRemove={() => {
                            setActions(actions => removeNested(actions, [...parentIdx, idx]));
                        }}
                        onActionChange={(aIdx: number, newAction: any) => {
                            setActions(actions => updateNested(actions, [...parentIdx, idx, 'actions', aIdx], newAction));
                        }}
                        onActionRemove={(aIdx: number) => {
                            setActions(actions => removeNested(actions, [...parentIdx, idx, 'actions', aIdx]));
                        }}
                    />
                );
            }
            // Render normal action
            return (
                <ActionForm
                    key={key}
                    action={action}
                    onChange={a => setActions(actions => updateNested(actions, [...parentIdx, idx], a))}
                    onRemove={() => setActions(actions => removeNested(actions, [...parentIdx, idx]))}
                    selectable={parentIdx.length === 0}
                    selected={parentIdx.length === 0 && selectedActions.includes(idx)}
                    onSelect={() => handleSelectAction(idx)}
                />
            );
        });
    }

    // Helper: update nested action/repeat block
    function updateNested(arr: any[], path: (string | number)[], value: any): any[] {
        if (path.length === 1) {
            const newArr = [...arr];
            newArr[path[0] as number] = value;
            return newArr;
        }
        const [head, ...rest] = path;
        const newArr = [...arr];
        if (typeof head === 'number') {
            newArr[head] = updateNested(newArr[head], rest, value);
        } else if (head === 'actions') {
            newArr[rest[0] as number].actions = updateNested(newArr[rest[0] as number].actions, rest.slice(1), value);
        }
        return newArr;
    }
    // Helper: remove nested action/repeat block
    function removeNested(arr: any[], path: (string | number)[]): any[] {
        if (path.length === 1) {
            return arr.filter((_, i) => i !== path[0]);
        }
        const [head, ...rest] = path;
        const newArr = [...arr];
        if (typeof head === 'number') {
            newArr[head] = removeNested(newArr[head], rest);
        } else if (head === 'actions') {
            newArr[rest[0] as number].actions = removeNested(newArr[rest[0] as number].actions, rest.slice(1));
        }
        return newArr;
    }

    const strategy = {
        trigger: { type: trigger, value: triggerValue },
        actions,
    };

    return (
        <div className="flex flex-col md:flex-row gap-8 p-6 text-white min-h-[80vh]">
            {/* Left: Builder or JSON Editor */}
            <div className="md:w-1/2 w-full">
                {/* Saved strategies list */}
                <div className="mb-6">
                    <h2 className="text-lg font-semibold mb-2">Saved Strategies</h2>
                    <div className="space-y-2">
                        {savedStrategies.map((s, idx) => (
                            <div key={s.name} className="flex items-center gap-2 bg-gray-800 rounded p-2">
                                <button
                                    className="text-cyan-400 underline text-left flex-1 truncate"
                                    onClick={() => handleLoad(s)}
                                    title="Load strategy"
                                >
                                    {s.name}
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
                {/* Name input and save */}
                <div className="flex items-center gap-2 mb-6">
                    <input
                        className="bg-gray-900 text-white rounded p-2 flex-1"
                        placeholder="Strategy name..."
                        value={strategyName}
                        onChange={e => setStrategyName(e.target.value)}
                    />
                    <button
                        className="bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2 rounded font-semibold"
                        onClick={handleSave}
                        disabled={!strategyName.trim()}
                    >
                        Save
                    </button>
                </div>
                <div className="flex items-center gap-4 mb-6">
                    <button
                        className={`px-4 py-2 rounded font-semibold transition-colors ${mode === 'builder' ? 'bg-cyan-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-cyan-900'}`}
                        onClick={() => setMode('builder')}
                    >
                        Builder Tool
                    </button>
                    <button
                        className={`px-4 py-2 rounded font-semibold transition-colors ${mode === 'json' ? 'bg-cyan-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-cyan-900'}`}
                        onClick={() => setMode('json')}
                    >
                        JSON Editor
                    </button>
                </div>
                {mode === 'builder' ? (
                    <>
                        <h1 className="text-3xl font-bold mb-6">Strategy Builder</h1>
                        {/* Trigger selection */}
                        <div className="bg-gray-800 rounded-lg p-6 mb-8">
                            <label className="block text-lg font-semibold mb-2">Trigger</label>
                            <div className="flex gap-4 items-center">
                                <select
                                    className="bg-gray-900 text-white rounded p-2"
                                    value={trigger}
                                    onChange={e => { setTrigger(e.target.value); setTriggerValue(''); }}
                                >
                                    {TRIGGERS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                </select>
                                <input
                                    className="bg-gray-900 text-white rounded p-2 flex-1"
                                    type={trigger === 'time' ? 'datetime-local' : trigger === 'DCA' ? 'number' : 'number'}
                                    placeholder={
                                        trigger === 'health' ? 'Health Factor ≤ ...' :
                                            trigger === 'price' ? 'Price ≤ ...' :
                                                trigger === 'apy' ? 'APY ≤ ...' :
                                                    trigger === 'DCA' ? 'Number of Days' :
                                                        'Time (UTC)'
                                    }
                                    value={triggerValue}
                                    onChange={e => setTriggerValue(e.target.value)}
                                />
                            </div>
                        </div>
                        {/* Actions */}
                        <div className="mb-8">
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-lg font-semibold">Actions</label>
                                <div className="flex gap-2">
                                    <button
                                        className="bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2 rounded"
                                        onClick={handleAddAction}
                                    >
                                        + Add Action
                                    </button>
                                    <button
                                        className="bg-cyan-800 hover:bg-cyan-900 text-white px-4 py-2 rounded disabled:opacity-50"
                                        onClick={handleRepeatSelected}
                                        disabled={selectedActions.length === 0}
                                    >
                                        Repeat Selected
                                    </button>
                                </div>
                            </div>
                            {actions.length === 0 && <div className="text-gray-400 mb-2">No actions added yet.</div>}
                            {renderBuilderActions(actions)}
                        </div>
                        {/* Repeat Modal */}
                        {showRepeatModal && (
                            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                                <div className="bg-gray-900 rounded-lg p-6 w-full max-w-md">
                                    <h2 className="text-xl font-bold mb-4">Repeat Condition</h2>
                                    <RepeatConditionForm condition={repeatCondition} onChange={setRepeatCondition} />
                                    <div className="flex justify-end gap-2 mt-4">
                                        <button
                                            className="px-4 py-2 rounded bg-gray-700 text-gray-200 hover:bg-gray-600"
                                            onClick={() => setShowRepeatModal(false)}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            className="px-4 py-2 rounded bg-cyan-600 text-white hover:bg-cyan-700"
                                            onClick={handleApplyRepeat}
                                            disabled={!repeatCondition.type || !repeatCondition.value}
                                        >
                                            Apply
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <div>
                        <h1 className="text-3xl font-bold mb-6">Strategy JSON Editor</h1>
                        <textarea
                            className="w-full h-96 bg-gray-900 text-cyan-200 rounded p-4 font-mono text-sm"
                            value={json}
                            onChange={e => setJson(e.target.value)}
                            spellCheck={false}
                        />
                        {jsonError && <div className="text-red-400 mt-2">Invalid JSON: {jsonError}</div>}
                    </div>
                )}
            </div>
            {/* Right: Visualization */}
            <div className="md:w-1/2 w-full bg-gray-950 rounded-lg">
                <StrategyDiagram trigger={{ type: trigger, value: triggerValue }} actions={actions} />
            </div>
        </div>
    );
};

export default StrategyBuilder; 